import { http } from "../../utils/config";

const assetAPI = {
  // Get all with pagination and search
  getAssets: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = '',
      status = '',
      orgId = '',
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;
 
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(status !== '' && { status }),
      ...(orgId && { orgId })
    });

    return http.get(`/api/v1/assets?${queryParams}`);
  },

  getAssetAll: (orgId = null) => {
    return http.get(`/api/v1/assets/all`, { params: { orgId } });
  },

  // Get by ID
  getAssetById: (id) => {
    return http.get(`/api/v1/assets/${id}`);
  },

  // Số khoản thu chưa thanh toán sẽ đổi theo khi sửa tài sản này
  getAffectedFees: (id) => {
    return http.get(`/api/v1/assets/${id}/affected-fees`);
  },

  // Lịch sử gán khách hàng của tài sản, mới nhất trước
  getAssignmentHistories: (id) => {
    return http.get(`/api/v1/assets/${id}/assignment-histories`);
  },

  // Create new
  createAsset: (assetData) => {
    return http.post('/api/v1/assets', assetData);
  },

  // Update
  updateAsset: (id, assetData) => {
    return http.put(`/api/v1/assets/${id}`, assetData);
  },

  // Delete
  deleteAsset: (id) => {
    return http.delete(`/api/v1/assets/${id}`);
  },

  // Xóa nhiều tài sản đã tích chọn. Dùng POST thay vì DELETE-có-body
  // vì một số proxy loại bỏ body của request DELETE.
  bulkDeleteAssets: (ids) => {
    return http.post(`/api/v1/assets/bulk-delete`, { ids });
  },


  // Update status
  updateStatus: (id, status) => {
    return http.patch(`/api/v1/assets/${id}/status`, {
      status: status
    });
  },
};

// Helper functions for handling API responses
export const assetHelpers = {
  // Extract  data from API response
  extractAssetData: (response) => {
    if (response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract paginated list from API response
  extractAssetList: (response) => {
    if (response.data && response.data.success) {
      const data = response.data.data;
      return {
        assets: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Format data for form submission
  formatAssetData: (formData) => {
    return {
      name: formData.name?.trim(),
      description: formData.description?.trim(),
      representative_name: formData.representative_name?.trim(),
      representative_phone: formData.representative_phone?.trim(),
      use_representative_name: !!formData.use_representative_name,
      // house_number: formData.house_number?.trim(),
      street: formData.street?.trim(),
      organization_id: formData.organization_id,
      customer_id: formData.customer_id,
      status: formData.status ?? true,
    };
  },

  // Validate data before submission
  validateAssetData: (assetData) => {
    const errors = {};

    if (!assetData.name?.trim()) {
      errors.name = 'Tên tài sản không được để trống';
    }

    if (!assetData.organization_id) {
      errors.organization_id = 'Vui lòng chọn đơn vị';
    }

    if (!assetData.customer_id) {
      errors.customer_id = 'Vui lòng chọn khách hàng sở hữu';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response;
    } catch (error) {
      console.error('API Error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 401:
            throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
          case 403:
            throw new Error('Bạn không có quyền thực hiện thao tác này');
          case 404:
            throw new Error('Không tìm thấy khách hàng');
          case 422: {
            // StoreAssetRequest/UpdateAssetRequest trả {success:false, errors:{field:[...]}}
            // và KHÔNG có `message`, nên chỉ đọc data.message thì lỗi theo trường bị nuốt
            // mất, người dùng chỉ thấy câu chung chung. Gắn kèm để form hiện đúng ô.
            const validationError = new Error(data.message || 'Dữ liệu không hợp lệ');
            validationError.fieldErrors = data.errors || null;
            throw validationError;
          }
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || 'Đã có lỗi xảy ra');
        }
      } else if (error.request) {
        // Network error
        throw new Error('Không thể kết nối đến server');
      } else {
        // Other error
        throw new Error(error.message || 'Đã có lỗi xảy ra');
      }
    }
  };
};

// Wrapped API functions with error handling
export const assetAPIWithErrorHandling = {
  getAssets: withErrorHandling(assetAPI.getAssets),
  getAssetAll: withErrorHandling(assetAPI.getAssetAll),
  getAssetById: withErrorHandling(assetAPI.getAssetById),
  getAffectedFees: withErrorHandling(assetAPI.getAffectedFees),
  getAssignmentHistories: withErrorHandling(assetAPI.getAssignmentHistories),
  createAsset: withErrorHandling(assetAPI.createAsset),
  updateAsset: withErrorHandling(assetAPI.updateAsset),
  deleteAsset: withErrorHandling(assetAPI.deleteAsset),
  bulkDeleteAssets: withErrorHandling(assetAPI.bulkDeleteAssets),
  updateStatus: withErrorHandling(assetAPI.updateStatus),
};

export default assetAPI;